import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, set, update } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { compressImage, buildAvatarPath, AVATAR_CACHE_CONTROL } from '../utils/compressImage';
import { isWelcomeComplete } from '../utils/welcomeComplete';
import './WelcomeInfos.css';
import GenerativeBackground from './GenerativeOrbAnimation';
import { useNavigate } from 'react-router-dom';

const USE_CASES = [
    { id: 'email_automation', label: 'Email Automation' },
    { id: 'cold_outreach', label: 'Cold Outreach' },
    { id: 'crm', label: 'Customer Relationship Management' },
    { id: 'lead_generation', label: 'Lead Generation' },
    { id: 'newsletter', label: 'Newsletter & Updates' },
    { id: 'sales_follow_up', label: 'Sales Follow-Up' },
    { id: 'customer_onboarding', label: 'Customer Onboarding' },
    { id: 'event_marketing', label: 'Event Marketing' },
];

const NOT_ALLOWED = [
    'Sending unsolicited bulk emails (Spam)',
    'Purchasing or using third-party email lists without consent',
    'Misleading subject lines or deceptive content',
    'Ignoring unsubscribe requests',
];

// Compliance text for legal record - DO NOT MODIFY without updating version
const COMPLIANCE_TEXT_VERSION = '1.0.0';
const COMPLIANCE_TEXT = `By using BonusClub, I confirm that:
1. I act on behalf of a business entity.
2. I am responsible for the recipients, content and lawful use of all communications sent through the platform.
3. I have obtained all necessary consents from recipients in compliance with applicable laws (e.g., GDPR, CAN-SPAM, CASL).
4. BonusClub provides technical tools only and is not responsible for the content, delivery, or compliance of any communications sent through the platform.
I confirm and accept responsibility for my use of the BonusClub platform.`;

// Simple hash function for compliance text versioning
const generateHash = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    return Math.abs(hash).toString(16);
};

// IP für den Compliance-Nachweis kommt aus unserem eigenen Backend, nicht von
// einem Drittdienst — der Nachweis soll keine zusätzliche Datenübermittlung auslösen.
const IP_FETCH_TIMEOUT_MS = 4000;

const fetchUserIP = async () => {
    try {
        const callable = httpsCallable(functions, 'getClientIp');
        const result = await Promise.race([
            callable(),
            new Promise((_, reject) => {
                setTimeout(() => reject(new Error('IP timeout')), IP_FETCH_TIMEOUT_MS);
            }),
        ]);
        return result?.data?.ip || 'unknown';
    } catch (error) {
        console.error('Could not fetch IP:', error);
        return 'unknown';
    }
};

const WelcomeInfos = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [userPhoto, setUserPhoto] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    
    // Step 2 states
    const [selectedUseCases, setSelectedUseCases] = useState([]);
    const [acceptedCompliance, setAcceptedCompliance] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const navigate = useNavigate();

    useEffect(() => {
        if (!currentUser) return;
        const userRef = ref(db, `Users/${currentUser.uid}`);
        get(userRef).then((snapshot) => {
            const userData = snapshot.exists() ? snapshot.val() : null;
            if (isWelcomeComplete(userData)) {
                navigate('/unlock');
                return;
            }
            const details = userData?.basicUserInfos && typeof userData.basicUserInfos === 'object'
                ? userData.basicUserInfos
                : userData || {};
            if (details.firstName) setFirstName(details.firstName);
            if (details.lastName) setLastName(details.lastName);
            if (details.companyName) setCompanyName(details.companyName);
            if (details.userPhoto) setUserPhoto(details.userPhoto);
        }).catch(error => {
            console.error('Error fetching user details:', error);
        });
    }, [currentUser, navigate, db]);

    const handleUploadChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploading(true);
        // Same treatment as UserSettings — see src/utils/compressImage.js for why.
        const image = await compressImage(file);
        const storage = getStorage();
        const fileRef = storageRef(storage, buildAvatarPath(currentUser.uid, image.extension));
        const uploadTask = uploadBytesResumable(fileRef, image.blob, {
            contentType: image.contentType,
            cacheControl: AVATAR_CACHE_CONTROL,
        });

        uploadTask.on(
            'state_changed',
            (snapshot) => {},
            (error) => {
                console.error("Error uploading file:", error);
                setIsUploading(false);
            },
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    setUserPhoto(downloadURL);
                    setIsUploading(false);
                });
            }
        );
    };

    const toggleUseCase = (useCaseId) => {
        setSelectedUseCases(prev => 
            prev.includes(useCaseId)
                ? prev.filter(id => id !== useCaseId)
                : [...prev, useCaseId]
        );
    };

    const canProceedStep1 = firstName.trim() && lastName.trim() && companyName.trim() && acceptedTerms && !isUploading;
    const canProceedStep2 = acceptedCompliance; // Use cases are optional

    const goToStep2 = () => {
        if (canProceedStep1) {
            setCurrentStep(2);
        }
    };

    const goBackToStep1 = () => {
        setCurrentStep(1);
    };

    const saveUserDetails = async () => {
        if (currentUser && canProceedStep2 && !isSaving) {
            setIsSaving(true);
            setSaveError('');
            
            const userId = currentUser.uid;
            const fullName = `${firstName} ${lastName}`.trim();
            const timestamp = new Date().toISOString();
            
            // Collect compliance record data
            const userIP = await fetchUserIP();
            const userAgent = navigator.userAgent;
            const complianceTextHash = generateHash(COMPLIANCE_TEXT);
            
            // Compliance record for legal purposes
            const complianceRecord = {
                userId: userId,
                userEmail: currentUser.email,
                timestamp: timestamp,
                ipAddress: userIP,
                userAgent: userAgent,
                complianceTextVersion: COMPLIANCE_TEXT_VERSION,
                complianceTextHash: complianceTextHash,
                acceptedTerms: acceptedTerms,
                acceptedCompliance: acceptedCompliance,
                fullComplianceText: COMPLIANCE_TEXT,
            };
            
            try {
                const basicUserInfos = {
                    firstName,
                    lastName,
                    name: fullName,
                    companyName,
                    acceptedTerms,
                    termsAcceptedAt: timestamp,
                    userPhoto,
                    setupDate: timestamp,
                    useCases: selectedUseCases,
                    acceptedCompliance,
                    complianceAcceptedAt: timestamp,
                };

                // One update so a failed write cannot look like a completed onboarding.
                // Do not write isPremium / feature flags — RTDB validate rules allow those only for admins.
                const userRef = ref(db, `Users/${userId}`);
                await update(userRef, {
                    email: currentUser.email,
                    firstName,
                    lastName,
                    name: fullName,
                    companyName,
                    userPhoto: userPhoto,
                    signupTimestamp: timestamp,
                    hasSettings: false,
                    sessionCount: 1,
                    lastLogin: timestamp,
                    lastAccess: timestamp,
                    basicInfos: true,
                    acceptedTerms,
                    termsAcceptedAt: timestamp,
                    useCases: selectedUseCases,
                    acceptedCompliance,
                    complianceAcceptedAt: timestamp,
                    complianceTextVersion: COMPLIANCE_TEXT_VERSION,
                    complianceTextHash: complianceTextHash,
                    basicUserInfos,
                });

                try {
                    const complianceRef = ref(db, `Users/${userId}/complianceRecords/${Date.now()}`);
                    await set(complianceRef, complianceRecord);
                    const globalComplianceRef = ref(db, `ComplianceLogs/${userId}_${Date.now()}`);
                    await set(globalComplianceRef, complianceRecord);
                } catch (auditError) {
                    console.warn('Compliance audit log failed:', auditError);
                }

                navigate('/unlock');
            } catch (error) {
                console.error('Error saving user details:', error);
                setSaveError('Could not save your details. Please try again.');
                setIsSaving(false);
            }
        }
    };

    return (
        <div className="welcome-container">
            <GenerativeBackground />
            <img className="welcome-logo" src="./img/BonusClub.png" alt="BonusClub Logo" />
            <div className="welcome-step-indicator">
                <div className={`welcome-step-dot ${currentStep >= 1 ? 'active' : ''}`}>
                    <span>1</span>
                </div>
                <div className={`welcome-step-line ${currentStep >= 2 ? 'active' : ''}`}></div>
                <div className={`welcome-step-dot ${currentStep >= 2 ? 'active' : ''}`}>
                    <span>2</span>
                </div>
            </div>
            <div className="welcome-form-premium">
                    {currentStep === 1 && (
                        <div className="welcome-step-content">
                            <div className="welcome-header">
                                <h1>Welcome to BonusClub</h1>
                                <p>Let's set up your account to get started</p>
                            </div>

                            <div className="welcome-profile-setup-section">
                                <div className="welcome-profile-photo-section">
                                    <label className="welcome-profile-upload-container" htmlFor="welcome-profile-upload">
                                        <input 
                                            id="welcome-profile-upload"
                                            type="file" 
                                            accept="image/*" 
                                            onChange={handleUploadChange} 
                                            style={{ display: 'none'}} 
                                        />
                                        {isUploading ? (
                                            <div className="welcome-profile-upload-loading">
                                                <div className="welcome-profile-upload-spinner"></div>
                                                <span>Uploading...</span>
                                            </div>
                                        ) : userPhoto ? (
                                            <div className="welcome-profile-preview">
                                                <img src={userPhoto} alt="Profile" />
                                                <div className="welcome-profile-change-overlay">
                                                    <span>Change photo</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="welcome-profile-upload-placeholder">
                                                <div className="welcome-profile-upload-icon">
                                                    <img src="./img/user-two.svg" alt="Profile" />
                                                </div>
                                                <span>Add photo</span>
                                            </div>
                                        )}
                                        <span className="welcome-profile-camera-badge" aria-hidden="true">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                                <circle cx="12" cy="13" r="4" />
                                            </svg>
                                        </span>
                                    </label>
                                </div>

                                {/* Name Inputs Row */}
                                <div className="welcome-profile-name-row">
                                    <div className="welcome-profile-input-group">
                                        <label htmlFor="welcome-profile-firstname" className="welcome-profile-input-label">First Name</label>
                                        <input
                                            id="welcome-profile-firstname"
                                            type="text"
                                            value={firstName}
                                            onChange={(e) => setFirstName(e.target.value)}
                                            placeholder="John"
                                            className="welcome-profile-premium-input"
                                            required
                                        />
                                    </div>
                                    <div className="welcome-profile-input-group">
                                        <label htmlFor="welcome-profile-lastname" className="welcome-profile-input-label">Last Name</label>
                                        <input
                                            id="welcome-profile-lastname"
                                            type="text"
                                            value={lastName}
                                            onChange={(e) => setLastName(e.target.value)}
                                            placeholder="Doe"
                                            className="welcome-profile-premium-input"
                                            required
                                        />
                                    </div>
                                </div>

                                {/* Company Name Input */}
                                <div className="welcome-profile-input-group welcome-profile-full-width">
                                    <label htmlFor="welcome-profile-company" className="welcome-profile-input-label">Company Name</label>
                                    <input
                                        id="welcome-profile-company"
                                        type="text"
                                        value={companyName}
                                        onChange={(e) => setCompanyName(e.target.value)}
                                        placeholder="Your Company Inc."
                                        className="welcome-profile-premium-input"
                                        required
                                    />
                                </div>

                                {/* Terms & Conditions Checkbox */}
                                <div className="welcome-profile-terms-section">
                                    <label className="welcome-profile-terms-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={acceptedTerms}
                                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                                        />
                                        <span className="welcome-profile-checkmark"></span>
                                        <span className="welcome-profile-terms-text">
                                            I agree to the <a href="https://bonusclub.io/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="https://bonusclub.io/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <button 
                                type="button"
                                onClick={goToStep2} 
                                disabled={!canProceedStep1} 
                                className="altBtn altBtn--save welcome-cta"
                            >
                                Continue
                            </button>
                        </div>
                    )}

                    {/* STEP 2: Use Case Selection */}
                    {currentStep === 2 && (
                        <div className="welcome-step-content">
                            <div className="welcome-header">
                                <h1>How will you use BonusClub?</h1>
                                <p>Select all that apply to your business needs</p>
                            </div>

                            <div className="welcome-usecase-section">
                                {/* Use Case List */}
                                <div className="welcome-usecase-list">
                                    {USE_CASES.map((useCase) => (
                                        <label
                                            key={useCase.id}
                                            className={`welcome-usecase-item ${selectedUseCases.includes(useCase.id) ? 'selected' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedUseCases.includes(useCase.id)}
                                                onChange={() => toggleUseCase(useCase.id)}
                                            />
                                            <span className="welcome-usecase-checkbox">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                    <polyline points="20 6 9 17 4 12"></polyline>
                                                </svg>
                                            </span>
                                            <span className="welcome-usecase-label">{useCase.label}</span>
                                        </label>
                                    ))}
                                    
                                </div>

                                {/* Not Allowed Section */}
                                <div className="welcome-notallowed-section">
                                    <div className="welcome-notallowed-header">
                                        <svg className="welcome-notallowed-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                                        </svg>
                                        <span>What's not allowed and will lead to account termination:</span>
                                    </div>
                                    <ul className="welcome-notallowed-list">
                                        {NOT_ALLOWED.map((item, index) => (
                                            <li key={index}>{item}</li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Compliance Checkbox */}
                                <div className="welcome-compliance-section">
                                    <div className="welcome-compliance-intro">
                                        <p className="welcome-compliance-title">By using BonusClub, I confirm that:</p>
                                        <ul className="welcome-compliance-list">
                                            <li>I act on behalf of a business entity.</li>
                                            <li>I am responsible for the recipients, content and lawful use of all communications sent through the platform.</li>
                                            <li>I have obtained all necessary consents from recipients in compliance with applicable laws (e.g., GDPR, CAN-SPAM, CASL).</li>
                                            <li>BonusClub provides technical tools only and is not responsible for the content, delivery, or compliance of any communications sent through the platform.</li>
                                        </ul>
                                    </div>
                                    <label className="welcome-compliance-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={acceptedCompliance}
                                            onChange={(e) => setAcceptedCompliance(e.target.checked)}
                                        />
                                        <span className="welcome-compliance-checkmark"></span>
                                        <span className="welcome-compliance-text">
                                            I confirm and accept responsibility for my use of the BonusClub platform.
                                        </span>
                                    </label>
                                </div>
                            </div>

                            {saveError && (
                                <p className="welcome-save-error" role="alert">{saveError}</p>
                            )}
                            <div className="welcome-step2-buttons">
                                <button 
                                    type="button"
                                    onClick={goBackToStep1} 
                                    className="altBtn altBtn--petrol"
                                    disabled={isSaving}
                                >
                                    Back
                                </button>
                                <button 
                                    type="button"
                                    onClick={saveUserDetails} 
                                    disabled={!canProceedStep2 || isSaving} 
                                    className="altBtn altBtn--save welcome-cta"
                                >
                                    {isSaving ? 'Saving...' : 'Get Started'}
                                </button>
                            </div>
                        </div>
                    )}
            </div>
        </div>
    );
};

export default WelcomeInfos;
